/**
 * Get organization root folder path from organization key
 * @param {string} organizationKey - The organization key
 * @returns {string|null} - Root folder path for the organization (e.g., "/folder/1649")
 */
export const getOrganizationRootPath = (organizationKey) => {
  const envVarMapping = {
    'def': process.env.REACT_APP_SHAREPOINT_DEF_ROOT,
    '3f': process.env.REACT_APP_SHAREPOINT_3F_ROOT,
    'tl': process.env.REACT_APP_SHAREPOINT_TL_ROOT,
    'dm': process.env.REACT_APP_SHAREPOINT_DM_ROOT,
    'hk': process.env.REACT_APP_SHAREPOINT_HK_ROOT,
    'ds': process.env.REACT_APP_SHAREPOINT_DS_ROOT,
    'sl': process.env.REACT_APP_SHAREPOINT_SL_ROOT
  };
  
  return envVarMapping[organizationKey?.toLowerCase()] || null;
};

/**
 * Get organization folder ID from organization key
 * @param {string} organizationKey - The organization key
 * @returns {string|null} - Folder ID for the organization
 */
export const getOrganizationFolderId = (organizationKey) => {
  const envVarMapping = {
    'def': process.env.REACT_APP_SHAREPOINT_DEF_ROOT,
    '3f': process.env.REACT_APP_SHAREPOINT_3F_ROOT,
    'tl': process.env.REACT_APP_SHAREPOINT_TL_ROOT,
    'dm': process.env.REACT_APP_SHAREPOINT_DM_ROOT,
    'hk': process.env.REACT_APP_SHAREPOINT_HK_ROOT,
    'ds': process.env.REACT_APP_SHAREPOINT_DS_ROOT,
    'sl': process.env.REACT_APP_SHAREPOINT_SL_ROOT
  };

  const folderPath = envVarMapping[organizationKey?.toLowerCase()];
  if (!folderPath) return null;

  // Extract folder ID from path like "/folder/1649"
  const match = folderPath.match(/\/folder\/(\d+)/);
  return match ? match[1] : null;
};

/**
 * Filter folder structure to show only folders relevant to the specified organization
 * @param {Object} folderStructure - The complete folder structure
 * @param {string} organizationKey - The organization key (def, 3f, tl, etc.)
 * @returns {Object|null} - Filtered folder structure or null if not found
 */
export const filterFoldersByOrganization = (folderStructure, organizationKey) => {
  if (!folderStructure || !organizationKey) {
    return folderStructure;
  }

  const organizationFolderId = getOrganizationFolderId(organizationKey);
  if (!organizationFolderId) {
    console.warn(`No folder ID found for organization: ${organizationKey}`);
    return folderStructure;
  }

  // Find the organization folder by matching folder ID
  const findOrganizationFolder = (folder) => {
    // Check if this folder matches the organization folder ID
    if (folder.Id === organizationFolderId) {
      return folder;
    }

    // Recursively search in children
    if (folder.Children) {
      for (const child of folder.Children) {
        const found = findOrganizationFolder(child);
        if (found) return found;
      }
    }

    return null;
  };

  // Search for the organization folder in the structure
  if (folderStructure.Children) {
    for (const topLevelFolder of folderStructure.Children) {
      const orgFolder = findOrganizationFolder(topLevelFolder);
      if (orgFolder) {
        // Return a structure that makes this folder appear as the root
        return {
          ...folderStructure,
          Children: [orgFolder]
        };
      }
    }
  }

  // If no specific organization folder found, return empty structure
  console.warn(`No folder found for organization: ${organizationKey} with ID: ${organizationFolderId}`);
  return {
    ...folderStructure,
    Children: []
  };
};

/**
 * Get organization name from organization key
 * @param {string} organizationKey - The organization key
 * @returns {string} - Human readable organization name
 */
export const getOrganizationName = (organizationKey) => {
  const names = {
    'def': 'Dansk EL-Forbund',
    '3f': '3F',
    'tl': 'Teknisk Landsforbund', 
    'dm': 'Dansk Metal',
    'hk': 'HK Kommunal',
    'ds': 'Dansk Socialrådgiverforening',
    'sl': 'Socialpædagogerne'
  };
  
  return names[organizationKey?.toLowerCase()] || organizationKey;
};

/**
 * Get organization folder name and path from folder structure
 * @param {Object} folderStructure - The complete folder structure
 * @param {string} organizationKey - The organization key
 * @returns {Object|null} - Object with {name, path, serverRelativeUrl} or null if not found
 */
export const getOrganizationFolderInfo = (folderStructure, organizationKey) => {
  if (!folderStructure || !organizationKey) {
    return null;
  }

  const organizationFolderId = getOrganizationFolderId(organizationKey);
  if (!organizationFolderId) {
    return null;
  }

  // Find the organization folder by matching folder ID
  const findOrganizationFolder = (folder) => {
    // Check if this folder matches the organization folder ID
    if (folder.Id === organizationFolderId) {
      // Return just the folder name, no paths or slashes
      return {
        name: folder.Name,
        cleanName: folder.Name, // Just the folder name without any path components
        path: folder.FileUrl || folder.ServerRelativeUrl,
        serverRelativeUrl: folder.ServerRelativeUrl,
        id: folder.Id
      };
    }

    // Recursively search in children
    if (folder.Children) {
      for (const child of folder.Children) {
        const found = findOrganizationFolder(child);
        if (found) return found;
      }
    }

    return null;
  };

  // Search for the organization folder in the structure
  if (folderStructure.Children) {
    for (const topLevelFolder of folderStructure.Children) {
      const orgFolder = findOrganizationFolder(topLevelFolder);
      if (orgFolder) {
        return orgFolder;
      }
    }
  }

  return null;
};

/**
 * Check if we're currently in organization context
 * @param {string} pathname - Current location pathname
 * @returns {string|null} - Organization key if in org context, null otherwise
 */
export const getOrganizationFromPath = (pathname) => {
  const match = pathname.match(/^\/org\/([^\/]+)/);
  return match ? match[1] : null;
};